import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import { logEvent } from '../../utils/analytics';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const scrollToSection = (href: string) => {
    logEvent('Navigation', 'Section Click', href);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const changeLanguage = (lng: string) => {
    logEvent('Language', 'Change', lng);
    i18n.changeLanguage(lng);
  };

  const navigationLinks = [
    { name: t('nav.home'), href: '#home' },
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.techStack'), href: '#tech-stack' },
    { name: t('nav.about'), href: '#about' },
    { name: t('nav.contact'), href: '#contact' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-xl border-b border-blue-100 w-full">
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        <div className="flex justify-between items-center h-16 w-full">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0 min-w-0">
            <img 
              src="https://static.readdy.ai/image/0c50aed3eb71487d515cf1f4733a4b77/dca41deac6d1ef7d0bf8c29028228eab.png" 
              alt="Balam Technology Logo" 
              className="h-7 sm:h-8 md:h-10 w-auto transition-transform duration-300 hover:scale-105 drop-shadow-lg"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            {navigationLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className="text-blue-700 hover:text-blue-900 transition-all duration-300 font-medium cursor-pointer relative group whitespace-nowrap text-sm xl:text-base"
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-blue-600 to-blue-800 transition-all duration-300 group-hover:w-full"></span>
              </button>
            ))}
            
            {/* Language Switcher */}
            <div className="flex items-center space-x-1 bg-white/80 backdrop-blur-sm rounded-full p-1 shadow-lg border border-blue-100">
              <button
                onClick={() => changeLanguage('en')}
                className={`px-2 xl:px-3 py-1.5 text-xs xl:text-sm font-medium rounded-full cursor-pointer transition-all duration-300 whitespace-nowrap ${
                  i18n.language === 'en' 
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md transform scale-105' 
                    : 'text-blue-700 hover:bg-blue-50 hover:text-blue-900'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => changeLanguage('es')}
                className={`px-2 xl:px-3 py-1.5 text-xs xl:text-sm font-medium rounded-full cursor-pointer transition-all duration-300 whitespace-nowrap ${
                  i18n.language === 'es' 
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md transform scale-105' 
                    : 'text-blue-700 hover:bg-blue-50 hover:text-blue-900'
                }`}
              >
                ES
              </button>
            </div>
            
            <Button 
              size="sm" 
              onClick={() => scrollToSection('#contact')}
              className="shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap text-xs xl:text-sm px-3 xl:px-4"
            >
              {t('nav.getQuote')}
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex-shrink-0">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-blue-700 hover:text-blue-900 cursor-pointer p-2 rounded-lg hover:bg-white/20 transition-all duration-300 flex items-center justify-center"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl sm:text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-blue-100 shadow-xl absolute left-0 right-0 top-full">
            <div className="px-3 sm:px-4 pt-2 pb-4 space-y-2 max-h-screen overflow-y-auto">
              {navigationLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="block w-full text-left px-3 py-2.5 text-blue-700 hover:text-blue-900 hover:bg-blue-50 transition-all duration-300 font-medium cursor-pointer rounded-lg text-sm"
                >
                  {link.name}
                </button>
              ))}
              
              {/* Mobile Language Switcher */}
              <div className="flex items-center justify-center space-x-2 pt-3 pb-2">
                <button
                  onClick={() => changeLanguage('en')}
                  className={`px-4 py-2 text-sm font-medium rounded-full cursor-pointer transition-all duration-300 ${
                    i18n.language === 'en' 
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md' 
                      : 'text-blue-700 hover:bg-blue-50 border border-blue-200'
                  }`}
                >
                  EN
                </button>
                <button
                  onClick={() => changeLanguage('es')}
                  className={`px-4 py-2 text-sm font-medium rounded-full cursor-pointer transition-all duration-300 ${
                    i18n.language === 'es' 
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md' 
                      : 'text-blue-700 hover:bg-blue-50 border border-blue-200'
                  }`}
                >
                  ES
                </button>
              </div>
              
              <div className="pt-2">
                <Button 
                  size="sm" 
                  className="w-full shadow-lg text-sm whitespace-nowrap py-2.5"
                  onClick={() => scrollToSection('#contact')}
                >
                  {t('nav.getQuote')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
